import React, { createContext, useImperativeHandle, useRef, useState, useEffect } from 'react'
import BaseModal from '@/component/modal/BaseModal.tsx'
import useQueryGetAlertEmergencyLive, { IAlertEmergencyLiveItem, IResGetEmergencyLive } from '@/api/notification/getLiveEmergencyAlert.ts'
import useQueryGetAlertFallDetectionLive, { IAlertFallDetectionLiveItem, IFallDetectionLiveData } from '@/api/notification/getLiveFallDetectionAlert.ts'
import useQueryGetAlertEmergencyDetail from '@/api/notification/getAlertEmergencyDetail.ts'
import useQueryGetFallDetectionDetail from '@/api/notification/getFallDetectionDetail.ts'
import EmergencyAlertItem from '@/pages/notification/emergency/EmergencyAlertItem.tsx'
import EmergencyAlertDetail from '@/pages/notification/emergency/EmergencyAlertDetail.tsx'
import { isEmptyArray, isNotEmptyArray } from '@/util/array.ts'
import { useTranslation } from 'react-i18next'
import { EmergencyAlertType } from '@/api/notification/getLiveEmergencyAlert.ts'
import { useAudioStore } from '@/store/AudioStore.ts'
import { useDismissedAlertStore } from '@/store/DismissedAlertStore.ts'
import useQueryGetMe from '@/api/auth/getMe.ts'


export interface IProps {
    className?: string
    refetchAlerts?: () => void
    defaultId?: number
    defaultTab?: EmergencyAlertType
    sosAlerts?: IResGetEmergencyLive
    fallAlerts?: IFallDetectionLiveData
    isLiveMode?: boolean
}

export type IEmergencyLiveAlertContextProps = {
    id?: number
    setId: (id: number) => void
    refetchList: () => void
    refetchAndResetSelection: () => void
    activeTab: EmergencyAlertType
    setActiveTab: (tab: EmergencyAlertType) => void
}

export const EmergencyLiveAlertContext = createContext<IEmergencyLiveAlertContextProps>({
    id: undefined,
    setId: () => {
    },
    refetchList: () => {
    },
    refetchAndResetSelection: () => {
    },
    activeTab: EmergencyAlertType.SOS,
    setActiveTab: () => {
    }
})

const EmergencyAlertModal = ({ defaultId, refetchAlerts, defaultTab, sosAlerts: propSosAlerts, fallAlerts: propFallAlerts, isLiveMode = true }: IProps, ref: any) => {
    const { t } = useTranslation()
    const { play, stop } = useAudioStore()
    const { dismissAlerts } = useDismissedAlertStore()
    const { data: me } = useQueryGetMe()

    const modalRef = useRef<any>()
    const [id, setId] = useState<number>()
    const [activeTab, setActiveTab] = useState<EmergencyAlertType>(defaultTab || EmergencyAlertType.SOS)
    const [isUserTabChange, setIsUserTabChange] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false)

    // SOS 알림 데이터
    const { data: liveSosAlerts, refetch: refetchSosList } = useQueryGetAlertEmergencyLive(activeTab === EmergencyAlertType.SOS && isLiveMode)
    // 낙상 감지 알림 데이터
    const { data: liveFallAlerts, refetch: refetchFallList } = useQueryGetAlertFallDetectionLive(activeTab === EmergencyAlertType.FALL && isLiveMode)

    const sosAlerts = isLiveMode ? liveSosAlerts : propSosAlerts
    const fallAlerts = isLiveMode ? liveFallAlerts : propFallAlerts

    const { data: emergencyDetail } = useQueryGetAlertEmergencyDetail(activeTab === EmergencyAlertType.SOS ? id : undefined, isModalOpen)
    const { data: fallDetail } = useQueryGetFallDetectionDetail(activeTab === EmergencyAlertType.FALL ? id : undefined, isModalOpen)


    // 초기 마운트 시 데이터 상태에 따라 탭 설정
    useEffect(() => {
        if (isLiveMode) {
            const hasEmergencyAlerts = sosAlerts?.emergencies && sosAlerts.emergencies.length > 0
            const hasFallAlerts = fallAlerts?.falls && fallAlerts.falls.length > 0

            // 사용자가 직접 탭을 변경하지 않았고, 초기 로드 시점일 때만
            if (!isUserTabChange && !id) {
                if (!hasEmergencyAlerts && hasFallAlerts) {
                    setActiveTab(EmergencyAlertType.FALL)
                } else if (hasEmergencyAlerts) {
                    setActiveTab(EmergencyAlertType.SOS)
                }
            }
        }
    }, [sosAlerts, fallAlerts, isLiveMode, id, isUserTabChange])

    const refetchList = () => {
        if (activeTab === EmergencyAlertType.SOS) {
            refetchSosList()
        } else {
            refetchFallList()
        }
    }

    const getFilteredAlerts = (): (IAlertEmergencyLiveItem | IAlertFallDetectionLiveItem)[] => {
        if (activeTab === EmergencyAlertType.SOS) {
            if (!sosAlerts?.emergencies || sosAlerts.emergencies.length === 0) return []
            return sosAlerts.emergencies.filter(item => 
                item?.alert?.type === EmergencyAlertType.SOS || !item?.alert?.type
            )
        } else {
            if (!fallAlerts?.falls || fallAlerts.falls.length === 0) return []
            return fallAlerts.falls
        }
    }

    const hasAnyAlerts = (): boolean => {
        const sosAlertCount = sosAlerts?.emergencies?.filter(item => 
            item?.alert?.type === EmergencyAlertType.SOS || !item?.alert?.type
        ).length || 0;
        const fallAlertCount = fallAlerts?.falls?.length || 0;
        return sosAlertCount > 0 || fallAlertCount > 0;
    }

    const handleTabChange = (tab: EmergencyAlertType) => {
        setIsUserTabChange(true)
        setActiveTab(tab)
        setId(undefined)
    }

    const refetchAndResetSelection = async () => {
        if (!isLiveMode) return;

        let newAlerts: any[] = []
        let resAlerts: any;

        if (activeTab === EmergencyAlertType.SOS) {
            const { data } = await refetchSosList()
            resAlerts = data
            newAlerts = resAlerts?.emergencies || []
        } else {
            const { data } = await refetchFallList()
            resAlerts = data
            newAlerts = resAlerts?.falls || []
        }

        // activeTab에 따라 필터링 로직이 달라질 수 있음
        const filteredAlerts = activeTab === EmergencyAlertType.SOS 
            ? newAlerts?.filter((item: IAlertEmergencyLiveItem) => 
                item?.alert?.type === EmergencyAlertType.SOS || !item?.alert?.type
            )
            : newAlerts;

        if (isEmptyArray(filteredAlerts) && !hasAnyAlerts()) {
            // modalRef.current.close() // <--- 이 부분을 주석 처리하여 자동으로 닫히지 않도록 합니다.
            return
        }

        // 현재 탭의 알림이 없고 사용자가 직접 탭을 변경하지 않은 경우에만 자동 전환
        if (isEmptyArray(filteredAlerts) && !isUserTabChange) {
            const otherTabHasAlerts = activeTab === EmergencyAlertType.SOS 
                ? (fallAlerts?.falls && fallAlerts.falls.length > 0)
                : (sosAlerts?.emergencies && sosAlerts.emergencies.length > 0)
            
            if (otherTabHasAlerts) {
                setActiveTab(activeTab === EmergencyAlertType.SOS ? EmergencyAlertType.FALL : EmergencyAlertType.SOS)
                return
            }
        }

        setIsUserTabChange(false)

        const myAlerts = filteredAlerts?.filter((item: IAlertEmergencyLiveItem | IAlertFallDetectionLiveItem) => item?.alert?.isMyTask == true)
        if (myAlerts && isNotEmptyArray(myAlerts)) {
            setId(myAlerts[0].id)
            return
        }

        const notMyAlerts = filteredAlerts?.filter((item: IAlertEmergencyLiveItem | IAlertFallDetectionLiveItem) => !item?.alert?.taskManagerAt)
        if (notMyAlerts && isNotEmptyArray(notMyAlerts)) {
            setId(notMyAlerts[0].id)
            return
        }

        if (isNotEmptyArray(filteredAlerts)) {
            setId(filteredAlerts[0].id)
        } else {
            setId(undefined)
        }
    }

    useImperativeHandle(ref, () => ({
        show: () => {
            modalRef.current.show()
        },
        close: () => {
            modalRef.current.close()
        }
    }))

    // activeTab이 변경될 때 ID를 초기화하고 목록을 다시 로드하여 첫 번째 항목을 선택하도록 합니다.
    useEffect(() => {
        if (isLiveMode) {
            refetchAndResetSelection();
        }
    }, [activeTab, isLiveMode]);

    // defaultId가 변경될 때 선택된 항목 업데이트
    useEffect(() => {
        if (defaultId !== undefined) {
            setId(defaultId);
            modalRef.current?.show();
        }
    }, [defaultId]);

    useEffect(() => {
        if (isLiveMode) {
            const alerts = getFilteredAlerts()
            if (isEmptyArray(alerts)) {
                modalRef.current?.close()
            }
        }
    }, [sosAlerts, fallAlerts, isLiveMode])

    useEffect(() => {
        const detailData = activeTab === EmergencyAlertType.SOS ? emergencyDetail : fallDetail;
        if (me?.email === 'ktt@carearly.com' && detailData?.alert?.confirm === '미처리') {
            play();
        } else {
            stop();
        }
    }, [id, emergencyDetail, fallDetail, activeTab, me]);

    return (
        <EmergencyLiveAlertContext.Provider value={{ 
            id, 
            setId, 
            refetchList, 
            refetchAndResetSelection,
            activeTab,
            setActiveTab: handleTabChange
        }}>
            <BaseModal
                ref={modalRef}
                width={'1200'}
                panelClassName={'animate-blinking-background'}
                onOpenCallback={() => {
                    setIsUserTabChange(false)
                    setIsModalOpen(true)
                    
                    if (isLiveMode) {
                        const currentFilteredAlerts = getFilteredAlerts()
                        if (defaultId && currentFilteredAlerts.find(alert => alert.id === defaultId)) {
                            setId(defaultId)
                        } else if (isNotEmptyArray(currentFilteredAlerts)) {
                            setId(currentFilteredAlerts[0]!.id)
                        } else {
                            // 현재 탭에 알림이 없을 때 다른 탭 확인
                            const otherTabHasAlerts = activeTab === EmergencyAlertType.SOS
                                ? fallAlerts?.falls
                                : sosAlerts?.emergencies

                            if (isNotEmptyArray(otherTabHasAlerts)) {
                                setActiveTab(activeTab === EmergencyAlertType.SOS ? EmergencyAlertType.FALL : EmergencyAlertType.SOS)
                            } else {
                                setId(undefined)
                            }
                        }
                    } else {
                        // 상세 조회 모드에서는 항상 defaultId를 사용
                        setId(defaultId)
                    }
                }}
                onCloseCallback={() => {
                    const unprocessedAlerts = getFilteredAlerts()
                    const unprocessedAlertIds = unprocessedAlerts.map(alert => alert.id)
                    if (unprocessedAlertIds.length > 0) {
                        dismissAlerts(unprocessedAlertIds)
                    }

                    stop()
                    setId(undefined)
                    setIsUserTabChange(false)
                    setIsModalOpen(false)
                    refetchAlerts && refetchAlerts()
                }}
                showCloseButton={false}>

                <div className={'p-[23px] min-h-[670px]'}>
                    <div className={`grid ${isLiveMode ? 'grid-cols-[324px_820px]' : 'grid-cols-[820px]'} gap-[10px] justify-center`}>
                        {/* Live 목록 */}
                        {isLiveMode && <div className={'flex flex-col p-[10px] justify-start items-start overflow-y-auto h-full max-h-[600px]'}>
                            <div className={'flex w-full mb-[10px]'}>
                                <div 
                                    className={`flex-1 text-center py-[5px] cursor-pointer rounded-tl-[5px] rounded-tr-[5px] ${activeTab === EmergencyAlertType.SOS ? 'bg-sub_r text-white' : 'bg-gray2'}`}
                                    onClick={() => handleTabChange(EmergencyAlertType.SOS)}>
                                    {t('notification.sosModal.tab.sos')}
                                </div>
                                <div 
                                    className={`flex-1 text-center py-[5px] cursor-pointer rounded-tl-[5px] rounded-tr-[5px] ${activeTab === EmergencyAlertType.FALL ? 'bg-[#8B5CF6] text-white' : 'bg-gray2'}`}
                                    onClick={() => handleTabChange(EmergencyAlertType.FALL)}>
                                    {t('notification.sosModal.tab.fall')}
                                </div>
                            </div>
                            
                            <div
                                className={`text-[20px] ${activeTab === EmergencyAlertType.FALL ? 'bg-[#8B5CF6]' : 'bg-sub_r'} text-white px-[15px] py-[5px] rounded-[5px] w-full`}>
                                {activeTab === EmergencyAlertType.SOS 
                                    ? t('notification.sosModal.title') 
                                    : t('notification.sosModal.title.fall')}
                            </div>
                            <div className={'mt-[10px] flex flex-col gap-[10px] w-full h-full overflow-y-auto'}>
                                {getFilteredAlerts().length > 0 
                                    ? getFilteredAlerts().map((item: IAlertEmergencyLiveItem | IAlertFallDetectionLiveItem) =>
                                        <EmergencyAlertItem
                                            key={item?.id} 
                                            item={item} 
                                            alertType={activeTab}
                                            />)
                                    : <div className={'grow flex items-center justify-center'}>
                                        <p>{t('notification.sosModal.empty')}</p>
                                    </div>}
                            </div>
                        </div>}

                        {/* 알림 상세 내용 */}
                        <EmergencyAlertDetail />
                    </div>
                </div>
            </BaseModal>
        </EmergencyLiveAlertContext.Provider>
    )
}

export default React.forwardRef(EmergencyAlertModal)
